import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, AlertTriangle, AlertOctagon, Users, Key, Globe,
  FileText, Download, ChevronRight, Clock, Server, Lock,
  CheckCircle, XCircle, ArrowRight
} from 'lucide-react';
import { getFirstScanReport, downloadFirstScanPDF } from '../services/api';

const severityConfig = {
  critical: { color: '#FF3B30', bg: 'bg-[#FF3B30]/10', border: 'border-[#FF3B30]/30', label: 'CRITICAL' },
  high: { color: '#FF6961', bg: 'bg-[#FF6961]/10', border: 'border-[#FF6961]/30', label: 'HIGH' },
  medium: { color: '#FFCC00', bg: 'bg-[#FFCC00]/10', border: 'border-[#FFCC00]/30', label: 'MEDIUM' },
  low: { color: '#34C759', bg: 'bg-[#34C759]/10', border: 'border-[#34C759]/30', label: 'LOW' },
};

const FirstScanPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const resp = await getFirstScanReport();
      setReport(resp.data);
    } catch (err) {
      console.error('Failed to load first scan report', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#9CA3AF]">Generating scan report...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-[#9CA3AF]">Failed to generate scan report</p>
      </div>
    );
  }

  const { summary, findings, risk_breakdown, top_risk_agents } = report;
  const overallRisk = summary.critical_violations > 0 ? 'critical' : summary.high_violations > 0 ? 'high' : 'medium';

  return (
    <div data-testid="first-scan-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <FileText className="w-6 h-6 text-[#0066FF]" />
            <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              First Scan Report
            </h1>
          </div>
          <p className="text-sm text-[#6B7280]">
            Scan completed in {report.scan_duration_seconds}s &middot; {summary.connectors_scanned} connectors &middot; {summary.total_identities} identities discovered
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              setPdfLoading(true);
              try {
                const resp = await downloadFirstScanPDF();
                const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `first-scan-report-${new Date().toISOString().split('T')[0]}.pdf`; a.click();
                URL.revokeObjectURL(url);
              } catch (err) { console.error('PDF download failed', err); }
              finally { setPdfLoading(false); }
            }}
            disabled={pdfLoading}
            className="primary-btn text-sm flex items-center gap-2"
            data-testid="export-scan-pdf-btn"
          >
            <FileText className="w-4 h-4" /> {pdfLoading ? 'Generating...' : 'Download PDF'}
          </button>
          <button
            onClick={() => {
              const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'first-scan-report.json'; a.click();
            }}
            className="secondary-btn text-sm flex items-center gap-2"
            data-testid="export-scan-report-btn"
          >
            <Download className="w-4 h-4" /> Export JSON
          </button>
        </div>
      </div>

      {/* Risk Banner */}
      <div className={`p-5 border ${severityConfig[overallRisk].border} ${severityConfig[overallRisk].bg}`} data-testid="risk-banner">
        <div className="flex items-center gap-3">
          <AlertOctagon className="w-6 h-6" style={{ color: severityConfig[overallRisk].color }} />
          <div>
            <div className="text-[#F9FAFB] font-semibold text-base">
              {summary.critical_violations + summary.high_violations} Critical & High-Severity Findings
            </div>
            <div className="text-sm text-[#9CA3AF]">
              {summary.identities_without_owner} orphaned identities &middot; {summary.excessive_privileges} with excessive privileges &middot; {summary.shared_credentials} shared credentials
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Identities', value: summary.total_identities, icon: Users, color: '#0066FF' },
          { label: 'Production', value: summary.production_identities, icon: Server, color: '#F9FAFB' },
          { label: 'No Owner', value: summary.identities_without_owner, icon: XCircle, color: '#FF3B30' },
          { label: 'Excess Privileges', value: summary.excessive_privileges, icon: Key, color: '#FF6961' },
          { label: 'Violations', value: summary.total_violations, icon: AlertTriangle, color: '#FFCC00' },
          { label: 'Policies', value: summary.policies_evaluated, icon: Shield, color: '#34C759' },
        ].map((m, i) => (
          <div key={i} className="enterprise-card p-4">
            <m.icon className="w-4 h-4 mb-2" style={{ color: m.color }} />
            <div className="text-2xl font-bold font-mono" style={{ color: m.color }}>{m.value}</div>
            <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Risk Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="enterprise-card p-6">
          <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">Risk Distribution</h2>
          <div className="space-y-3">
            {Object.entries(risk_breakdown).map(([level, count]) => {
              const cfg = severityConfig[level];
              const pct = summary.total_identities > 0 ? Math.round((count / summary.total_identities) * 100) : 0;
              return (
                <div key={level}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs uppercase font-mono" style={{ color: cfg.color }}>{cfg.label}</span>
                    <span className="text-xs font-mono text-[#F9FAFB]">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full h-2 bg-[#1A1A1A]">
                    <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Risk Agents */}
        <div className="enterprise-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider">Highest Risk Identities</h2>
            <Link to="/dashboard/inventory" className="text-[#0066FF] text-xs hover:text-[#3385FF]">View All</Link>
          </div>
          <div className="space-y-2">
            {top_risk_agents.map((agent, i) => (
              <Link
                key={agent.id}
                to={`/dashboard/agents/${agent.id}`}
                className="flex items-center justify-between p-3 bg-[#1A1A1A] hover:bg-[#222] transition-colors"
                data-testid={`risk-agent-${agent.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-[#6B7280] font-mono text-xs w-5">#{i + 1}</span>
                  <div>
                    <div className="text-[#F9FAFB] font-mono text-sm">{agent.name}</div>
                    <div className="text-[10px] text-[#6B7280]">
                      {agent.owner || 'No Owner'} &middot; {agent.environment}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-lg font-bold text-[#FF3B30]">{agent.risk_score}</span>
                  <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Findings */}
      <div className="enterprise-card p-6">
        <h2 className="text-sm font-bold text-[#9CA3AF] uppercase tracking-wider mb-4">
          Findings ({findings.length})
        </h2>
        <div className="space-y-3">
          {findings.map((f, i) => {
            const cfg = severityConfig[f.severity] || severityConfig.medium;
            return (
              <div key={i} className={`p-4 border ${cfg.border} ${cfg.bg}`} data-testid={`finding-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5" style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                        {cfg.label}
                      </span>
                      <span className="text-[10px] text-[#6B7280] uppercase">{f.category}</span>
                    </div>
                    <div className="text-sm text-[#F9FAFB] font-medium">{f.title}</div>
                    <div className="text-xs text-[#9CA3AF] mt-1">{f.detail}</div>
                  </div>
                  <div className="ml-4 text-right shrink-0">
                    <div className="text-[10px] text-[#6B7280] uppercase mb-1">Action</div>
                    <div className="text-xs text-[#0066FF]">{f.recommendation}</div>
                  </div>
                </div>
                {f.agent_id && (
                  <div className="mt-2 pt-2 border-t border-[#27272A]/30">
                    <Link to={`/dashboard/agents/${f.agent_id}`} className="text-[10px] text-[#0066FF] hover:text-[#3385FF] flex items-center gap-1">
                      View Agent Details <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FirstScanPage;
